import {
  blogPosts,
  getLocalizedField,
  getLocalizedTags,
  type BlogPost,
} from "@/data/blogPosts";
import { getBlogSecondAnswerOverrideBySlug } from "@/data/blogFaqAnswerOverrides";
import { getBlogFirstQuestionBySlug } from "@/data/blogFaqFirstQuestions";
import { getLocalizedCaseStudyBySlug } from "@/data/caseStudies";
import { getExpectedFaqCountForRoutePath } from "@/lib/faqCoverageConfig";
import {
  INDEXABLE_ROUTE_PATHS,
  STATIC_ROUTE_PATHS,
} from "@/lib/routeInventory";
import {
  getLocalizedFaqSource,
  registerFaqLocale,
  type LocalizedFaqSource,
  type LocalizedFaqTemplateSet,
  type BlogArchetypeTemplateKey,
  type TechnicalGainKey,
  type TechnicalRiskKey,
} from "@/lib/faqLocalizedStore";
import {
  SEO_LANGUAGES,
  normalizeSeoLanguage,
  type SeoLanguage,
} from "@/lib/seoMetadata";

export type FaqEntry = {
  question: string;
  answer: string;
};

export const COMPANY_NAME = "Shelpuk AI Technology Consulting";

/**
 * Character budget for the page meta description interpolated into FAQ answers.
 *
 * Exported so `faqContentPolicy.test.ts` can assert that no description is long
 * enough to be truncated, which would make its detector silently skip that
 * route rather than fail. A constant, not behaviour — the alternative is a
 * magic ``300`` duplicated in the test, which is the drift this repository
 * keeps paying for.
 */
export const PAGE_DESCRIPTION_SUMMARY_LENGTH = 300;

/**
 * The routes that get an FAQ block — re-exported from the indexable-URL
 * inventory, which is now the single owner of this list.
 *
 * This module composed the same 49 paths itself until CWS-24 moved them to
 * `src/lib/routeInventory.ts`. The name is kept because `faqResolver.test.ts`
 * imports it — the only consumer that does; `faqCoverage.test.ts`,
 * `faqContentPolicy.test.ts` and `scripts/audit-faq-coverage.mjs` reach the
 * routes through {@link getIndexedRoutePaths} instead, so renaming this export
 * would break one file, not four.
 *
 * Order is unchanged because the inventory composes the three classes with the
 * same expression the deleted code used. It *matters* because
 * `scripts/audit-faq-coverage.mjs` iterates the sequence and its report follows
 * it — so a future reordering of `INDEXABLE_ROUTE_PATHS` would rewrite that
 * report.
 *
 * "Indexed" here and "indexable" there denote the same set by construction.
 */
export const INDEXED_STATIC_ROUTE_PATHS = STATIC_ROUTE_PATHS;

/**
 * Register a locale payload for the FAQ resolver — re-exported (CWS-199)
 *
 * The audit script (`scripts/audit-faq-coverage.mjs`) seeds all five locales
 * before its per-language loop, and it must seed the **same module instance**
 * this resolver reads: its `importTsModule` helper builds a fresh module
 * graph per call, so importing the store through a second call would fill a
 * registry the resolver never consults. Re-exporting through this module —
 * the same instance the script already imports — is the established pattern
 * (`INDEXED_STATIC_ROUTE_PATHS` above).
 */
export { registerFaqLocale };

const INDEXED_ROUTE_PATH_SET = new Set(INDEXABLE_ROUTE_PATHS);

const COMPANY_PATTERN = new RegExp(
  COMPANY_NAME.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
  "gi",
);

/**
 * Substitute ``{placeholder}`` tokens in a template with their values.
 *
 * The replacement is a **function**, so what it returns is inserted verbatim:
 * `String.replace` neither rescans it for further tokens nor reads ``$&``-style
 * patterns in it. A post title containing ``{tags}`` is therefore emitted as
 * text, which is the property the two-pass blog interpolation relies on.
 *
 * That is also why the ``.replace(/\$/g, "$$$$")`` this call used to carry was
 * removed (CWS-115). It escaped nothing — a function replacement needs no
 * escaping — and simply doubled every ``$``, so a title reading
 * ``inference at $0.02`` rendered ``$$0.02`` in the answer and in that route's
 * ``FAQPage`` JSON-LD. No value in the corpus contains ``$`` today, so removing
 * it changes no rendered page; it was found while routing blog copy through
 * this function for the first time.
 *
 * @param template - Template text carrying zero or more ``{placeholder}`` tokens.
 * @param values - Placeholder values; a missing key resolves to the empty string.
 * @returns The template with every recognised token replaced.
 */
function fillTemplate(template: string, values: Record<string, string>) {
  return template.replace(/\{(\w+)\}/g, (_, key: string) => values[key] || "");
}

/**
 * Tidy the whitespace an interpolated template leaves behind.
 *
 * Collapses runs of **ASCII** whitespace and removes ASCII whitespace stranded
 * before punctuation by a placeholder that resolved to nothing.
 *
 * Deliberately blind to no-break spaces. French typography sets ``?``, ``!``,
 * ``;`` and ``:`` off with one, and ``\s`` matches U+00A0 and U+202F — so the
 * previous ``/\s([,.;:!?])/`` deleted every form of it and no French copy could
 * carry its own typography (CWS-109). Narrowing the class here, rather than
 * threading the language through :func:`composeAnswer`, keeps the function
 * language-agnostic: it normalises accidents and leaves authored characters
 * alone.
 *
 * The class is written as "all whitespace **except** U+00A0 and U+202F" rather
 * than as a list of ASCII characters, so every other exotic space ``\s``
 * matched -- U+1680, the U+2000-200A range, U+2028/9, U+205F, U+3000, U+FEFF --
 * keeps being normalised exactly as before. Only the two no-break spaces are
 * exempted, which is the whole of the intended change.
 *
 * A *run* of no-break spaces is still collapsed to one. Exempting them from the
 * first pass is what lets an authored one survive; it is not a licence to keep
 * two, which no typography wants and which the at-rest contract cannot see —
 * it asserts the character before the mark, and both of two are no-break
 * spaces.
 *
 * @param value - Raw template text, already interpolated.
 * @returns The text with ASCII whitespace normalised and trimmed.
 *
 * ⚠️ **The punctuation class below deliberately excludes ``%``.** CWS-133's
 * per-locale percent separator is a plain U+0020, and this function runs over
 * the *composed* answer after interpolation — so adding ``%`` to that class
 * would silently delete the separator from every German, French and Spanish
 * blog answer. `blogFaqProfileParity.test.ts`'s spacing case is what catches it.
 */
function cleanupWhitespace(value: string) {
  return value
    .replace(/[^\S\u00a0\u202f]+/g, " ")
    .replace(/[\u00a0\u202f]{2,}/g, "\u00a0")
    .replace(/[^\S\u00a0\u202f]+([,.;:!?])/g, "$1")
    .trim();
}

function stripMetadataSuffix(value: string) {
  return value.split("|")[0] || value;
}

function normalizeRoutePath(value: string) {
  return value.split(/[?#]/)[0].replace(/^\/+|\/+$/g, "");
}

function removeCompanyMentions(value: string) {
  return cleanupWhitespace(value.replace(COMPANY_PATTERN, ""));
}

function countCompanyMentions(value: string) {
  const matches = value.match(new RegExp(COMPANY_PATTERN.source, "g"));
  return matches ? matches.length : 0;
}

function enforceCompanyMentionPolicy(answer: string) {
  const mentionCount = countCompanyMentions(answer);

  if (mentionCount !== 1) {
    throw new Error(
      `FAQ answer policy violation: expected 1 company mention, got ${mentionCount}`,
    );
  }

  return answer;
}

function composeAnswer(base: string, companyClause: string) {
  const sanitizedBase = removeCompanyMentions(base).replace(/[.!?]+$/, "");
  const sanitizedClause = removeCompanyMentions(companyClause).replace(/[.!?]+$/, "");
  const answer = cleanupWhitespace(
    `${sanitizedBase}. ${COMPANY_NAME} ${sanitizedClause}.`,
  );

  return enforceCompanyMentionPolicy(answer);
}

function buildFaqSet(
  questions: string[],
  bases: string[],
  companyClauses: string[],
) {
  return questions.map((question, index) => ({
    question: cleanupWhitespace(question),
    answer: composeAnswer(bases[index], companyClauses[index]),
  }));
}

function buildFaqSetFromLocalizedTemplates(
  templates: LocalizedFaqTemplateSet,
  values: Record<string, string>,
) {
  return buildFaqSet(
    templates.questions.map((value) => fillTemplate(value, values)),
    templates.answers.map((value) => fillTemplate(value, values)),
    templates.companyClauses.map((value) => fillTemplate(value, values)),
  );
}

function toLocalizedRouteKey(routePath: string) {
  if (!routePath) {
    return "home";
  }

  return routePath.replace(/\//g, "__");
}

function sanitizeInterpolatedText(value: string) {
  return cleanupWhitespace(removeCompanyMentions(stripMetadataSuffix(value)));
}

function truncateAtNaturalBoundary(value: string, maxLength: number) {
  if (value.length <= maxLength) {
    return value;
  }

  const clipped = value.slice(0, maxLength).trim();
  const minBoundary = Math.floor(maxLength * 0.6);
  const sentenceBoundary = Math.max(
    clipped.lastIndexOf(". "),
    clipped.lastIndexOf("! "),
    clipped.lastIndexOf("? "),
    clipped.lastIndexOf("."),
    clipped.lastIndexOf("!"),
    clipped.lastIndexOf("?"),
  );

  if (sentenceBoundary >= minBoundary) {
    return clipped.slice(0, sentenceBoundary + 1).trim();
  }

  const wordBoundary = clipped.lastIndexOf(" ");
  const safeBoundary = wordBoundary >= minBoundary ? wordBoundary : clipped.length;
  // A word-boundary clip can land just after a clause mark, and the caller then
  // appends a sentence stop — rendering ",." (CWS-45). Drop the stranded mark.
  return clipped.slice(0, safeBoundary).trim().replace(/[\s,;:]+$/, "");
}

function summarize(value: string, maxLength = 220) {
  const normalized = sanitizeInterpolatedText(value);
  return truncateAtNaturalBoundary(normalized, maxLength);
}

/**
 * Close a value with a sentence stop so it can be spliced into surrounding prose.
 *
 * Meta descriptions are authored ending in a full stop, but the value that
 * reaches a template need not: :func:`stripMetadataSuffix` drops a
 * ``| <brand>`` tail *together with its terminal stop*, and
 * :func:`truncateAtNaturalBoundary` can clip mid-clause. The home description is
 * the only one carrying that suffix today, which is why it alone ran into the
 * clause after it (CWS-106).
 *
 * Applied only at the ``pageDescription`` call site rather than inside
 * :func:`summarize`, whose other callers interpolate ``;``- and ``,``-joined
 * **lists** rather than sentences and must not gain a full stop. No other
 * placeholder carries this guarantee — see `SYSTEM_DESIGN.md` §16.1b.
 *
 * @param value - The interpolated text, already summarized and sanitized.
 * @returns The value unchanged when it already ends in ``.``, ``!`` or ``?``;
 *     otherwise the value with a full stop appended.
 */
function endWithSentenceStop(value: string) {
  if (!value || /[.!?]$/.test(value)) {
    return value;
  }

  return `${value}.`;
}

type BlogFaqArchetype = "technical" | "strategy" | "companyNews";

/**
 * The locale template key each archetype reads its copy from.
 *
 * A map rather than a name computed from the archetype, so the template keys
 * stay greppable in both directions — `blogTechnical` finds this line, and so
 * does `technical`.
 */
const BLOG_ARCHETYPE_TEMPLATE_KEY: Record<BlogFaqArchetype, BlogArchetypeTemplateKey> = {
  technical: "blogTechnical",
  companyNews: "blogCompanyNews",
  strategy: "blogStrategy",
};

/**
 * Longest blog title that reaches a generated FAQ whole.
 *
 * Above it :func:`truncateTopic` clips the title at a word boundary and the
 * clipped form becomes that route's FAQ topic — so a long title changes FAQ copy
 * with nothing about the edit saying so. Exported because CWS-114 needed to
 * assert it from a test, and because the value had been written twice in this
 * file: as this function's default and again at its one call site.
 */
export const BLOG_FAQ_TOPIC_MAX_LENGTH = 96;

function truncateTopic(value: string, maxLength = BLOG_FAQ_TOPIC_MAX_LENGTH) {
  return truncateAtNaturalBoundary(value, maxLength);
}

/**
 * Choose which of the three template sets a blog post's FAQ block is built from.
 *
 * The only selector of the four that was already locale-independent: it reads
 * ``tags``, ``category`` and ``slug``, none of which is translated. CWS-133 gave
 * its slug pattern the same leading ``\b`` as the variant selectors, because the
 * defect class is identical and the blast radius here is larger — this chooses
 * the whole template set, so a false positive swaps a strategy post's entire FAQ
 * block rather than one sentence.
 *
 * Exported for `faqResolver.test.ts`. Nothing in the rendered corpus can prove
 * the boundary: it changes 0 of the 23 shipped archetypes, so a mutation that
 * removes it is equivalent on today's data and survives every behavioural test.
 * A direct call is the only instrument that reaches it.
 *
 * Args:
 *     post: The post whose FAQ block is being built.
 *
 * Returns:
 *     The archetype naming its template set.
 */
export function resolveBlogFaqArchetype(post: BlogPost): BlogFaqArchetype {
  if (post.tags.includes("Company News")) {
    return "companyNews";
  }

  // Boundaried for the reason the two variant selectors are (CWS-133), and this
  // one matters more: it chooses the *template set*, so a false positive swaps a
  // strategy post's whole FAQ block rather than one sentence. `lora` sits inside
  // "coLORAdo" and `train` inside "constraint" -- both plausible English slugs.
  // Measured at `82a2b10`: 0 of 23 archetypes change.
  const technicalSlugPattern =
    /\b(llama|llm|lora|swift|unsloth|gemma|falcon|mistral|smaug|tgi|claude|inference|fine-tun|coding)/i;
  if (post.category === "Technology" || technicalSlugPattern.test(post.slug)) {
    return "technical";
  }

  return "strategy";
}

/**
 * Find the metric claims a post advertises, as locale-independent text.
 *
 * Since CWS-133 the caller passes the post's **English** title and summary, so
 * one claim set serves all five locales and the ``withMetrics`` /
 * ``withoutMetrics`` branch cannot differ between them.
 * :func:`formatMetricClaims` applies each locale's own spacing before ``%`` at
 * render time, which is the only part of a claim that is not locale-independent.
 *
 * Exported for `faqResolver.test.ts`: the canonical form and the dedupe that
 * depends on it are pure logic, and `TEST_SUITE.md` §2 says to prove a claim at
 * the lowest layer that can. No rendered route reaches the duplicate case,
 * because no English summary in the corpus writes one figure two ways.
 *
 * Args:
 *     values: The text to scan, joined with a space before matching.
 *
 * Returns:
 *     Up to three distinct claims, lower-cased and canonical, in order.
 */
export function extractMetricClaims(...values: string[]) {
  const combined = values.join(" ");
  const matches = combined.match(/\b\d+(?:\.\d+)?\s?%|\b\d+(?:\.\d+)?x\b/gi) || [];

  // Canonical form -- no separator before `%` -- and canonical *before* the
  // `Set`, not after. Design review caught this: the match alternative admits
  // both "20%" and "20 %", so deduping the raw forms lets one claim through
  // twice ("20 %, 20 %, 30 %" in German) and burns one of the three slots.
  const normalized = matches.map((value) =>
    cleanupWhitespace(value.toLowerCase()).replace(/\s*%/, "%"),
  );
  return Array.from(new Set(normalized)).slice(0, 3);
}

/**
 * Choose which technical risk sentence a blog route gets, from its English title.
 *
 * The patterns are English and so is the input: since **CWS-133** the caller
 * passes the post's English title for every locale, so one article resolves one
 * profile in all five. Before that the input was the *localized* title, and a
 * locale writing ``inferenza`` fell through to ``"general"`` while English
 * resolved ``"inference"`` — the same article answering a different question
 * depending on the language it was read in.
 *
 * ⚠️ **Each alternative is anchored with a leading ``\b`` and no trailing one.**
 * Leading, because ``lora`` without it matched inside Italian *espLORAre* and
 * ``train`` matches inside *constraint*. Not trailing, because ``fine-tun`` must
 * still reach "fine-tuning" and ``train`` must still reach "training" — closing
 * both ends is the plausible wrong fix, and `faqResolver.test.ts` pins against it.
 *
 * Exported since CWS-128 for `blogFaqProfileParity.test.ts`, and since CWS-133
 * for the boundary cases: the post that carries the *esplorare* false positive
 * resolves the ``strategy`` archetype, whose templates interpolate neither
 * technical variant, so no rendered-output sweep can reach this function.
 *
 * Args:
 *     topic: The post's sanitized **English** title.
 *
 * Returns:
 *     The key naming the locale-file copy this route receives.
 */
export function resolveTechnicalRiskProfile(topic: string): TechnicalRiskKey {
  const normalized = topic.toLowerCase();
  if (/\b(inference|tgi|throughput|latency)/.test(normalized)) {
    return "inference";
  }

  if (/\b(fine-tun|fine tun|lora|unsloth|swift|train)/.test(normalized)) {
    return "training";
  }

  if (/\b(multilingual|tokenizer|llm practitioner|gemma|llama-2)/.test(normalized)) {
    return "evaluation";
  }

  return "general";
}

/**
 * Choose the topic family whose gain sentence fits this post.
 *
 * The keyword groups are the ones :func:`buildTechnicalGainSentence` carried
 * inline, once per locale, before CWS-115 moved its copy into the locale files.
 * They read the post's **English** title since CWS-133, for the reason and with
 * the boundary rule given on :func:`resolveTechnicalRiskProfile`.
 *
 * ⚠️ **These four keys are not the four on the risk map, deliberately** — gain
 * branches on the post's subject matter, risk on its failure mode. See the
 * warning on :const:`TECHNICAL_GAIN_KEYS` in `faqLocalizedStore.ts`.
 *
 * Exported for `faqResolver.test.ts`'s boundary cases only; the resolver's own
 * call site is below.
 *
 * @param topic - The post's sanitized **English** title.
 * @returns The key into ``blogVariants.technicalGain``.
 */
export function resolveTechnicalGainTopic(topic: string): TechnicalGainKey {
  const normalized = topic.toLowerCase();

  if (/\b(llama[ -]?3\.3|inference|tgi)/.test(normalized)) {
    return "inference";
  }

  if (/\b(claude|code generation|coding)/.test(normalized)) {
    return "coding";
  }

  if (/\b(unsloth|swift|fine-tun|lora|training)/.test(normalized)) {
    return "training";
  }

  return "general";
}

/**
 * The spacing each locale writes between a number and the percent sign.
 *
 * Since CWS-133 the claims interpolated into ``{metricsPhrase}`` are extracted
 * from the post's **English** summary, so the digits reaching a German page are
 * English digits. Their spacing is not: DIN 5008:2011-04 §8.7, the OQLF and the
 * RAE each prescribe a space before ``%``, while English (Chicago) and Italian
 * set it closed. ⚠️ The Italian row is an **observed convention** — it is what
 * all five locale files and `blogPosts.ts` already write — not a citation; no
 * Italian authority was found. Note also that ISO 31-0 and the SI prescribe a
 * space in *every* language, which the ``en`` and ``it`` values knowingly depart
 * from in favour of national typographic practice. Without this map the CWS-133 repair would trade a profile defect for a
 * typography defect in three buyer-facing locales — measured, not supposed: it
 * moves ``20 %, 30 %`` to ``20%, 30%`` on the Claude Code post in de, fr and es.
 *
 * ⚠️ **U+0020, not the no-break space those authorities actually prescribe.**
 * The first reason is binding: `faqLocaleOrthography.test.ts`'s
 * ``describe("FAQ copy source")`` holds every character on a non-comment line of
 * this module inside printable ASCII, and it is right to — a no-break space is
 * locale copy and belongs in the locale files. The second is that the titles and
 * summaries this phrase renders beside are themselves written with U+0020, so
 * U+00A0 here would make the page less internally consistent, not more.
 * Migrating the site's number typography is a separate change, not this one.
 *
 * Keyed by :type:`SeoLanguage`, so a sixth locale is a type error rather than a
 * silent fall-through to the English form.
 */
const PERCENT_SIGN_SEPARATOR: Record<SeoLanguage, string> = {
  en: "", // Chicago Manual of Style.
  de: " ", // DIN 5008:2011-04, section 8.7.
  fr: " ", // OQLF, Vitrine linguistique: "une espace insecable".
  it: "", // Observed convention only, no Italian authority found (see above).
  es: " ", // RAE: "se separa con espacio de la cifra que lo precede: 50 %".
};

/**
 * Render locale-independent metric claims with the locale's own percent spacing.
 *
 * Args:
 *     claims: The claims :func:`extractMetricClaims` found in the English text.
 *     lang: The locale these claims are being rendered into.
 *
 * Returns:
 *     The claims, each carrying that locale's separator before ``%``.
 */
function formatMetricClaims(claims: readonly string[], lang: SeoLanguage) {
  const separator = PERCENT_SIGN_SEPARATOR[lang];

  // The claim arrives canonical from `extractMetricClaims`, so this only ever
  // *adds* the locale's separator. `\s*` is kept so the function is total on any
  // input rather than silently right only for its one current caller.
  return claims.map((claim) => claim.replace(/\s*%/, `${separator}%`));
}

/**
 * The locale-independent text the three technical selectors read.
 *
 * A named pair rather than two more bare ``string`` parameters beside ``topic``
 * and ``summary``: the defect CWS-133 repairs was precisely that the selectors
 * and the rendered copy read the *same* two variables, so this type exists to
 * make "selector input, not reader-facing text" impossible to miss at the call
 * site and impossible to pass by accident in the wrong order.
 */
type BlogSelectorSource = {
  title: string;
  summary: string;
};

/**
 * Build the values the blog templates interpolate.
 *
 * The three sentences the resolver *selects* — the company-news lead and the
 * technical gain and risk lines — are filled here rather than in the template
 * pass, because each is itself a template: the lead carries ``{topic}`` and the
 * gain line carries ``{metricsPhrase}``. Filling them first keeps the outer
 * pass a single substitution over template text, never over an author's value.
 *
 * @param source - The locale's FAQ source.
 * @param topic - The post's localized, truncated title.
 * @param summary - The post's localized summary, already truncated.
 * @param tags - The post's localized tags, comma-joined.
 * @returns Every placeholder the three blog archetypes can carry.
 */
function buildBlogTemplateValues(
  source: LocalizedFaqSource,
  lang: SeoLanguage,
  topic: string,
  summary: string,
  tags: string,
  selector: BlogSelectorSource,
) {
  // Three selections, all from `selector` and none from `topic`/`summary`: that
  // separation is the whole of CWS-133.
  const metricClaims = extractMetricClaims(selector.title, selector.summary);
  const metricsPhrase = formatMetricClaims(metricClaims, lang).join(", ");
  const gain = source.blogVariants.technicalGain[resolveTechnicalGainTopic(selector.title)];

  return {
    topic,
    summary,
    tags,
    companyNewsLead: fillTemplate(source.blogVariants.companyNewsLead, { topic }),
    technicalGain: fillTemplate(
      metricsPhrase ? gain.withMetrics : gain.withoutMetrics,
      { metricsPhrase },
    ),
    technicalRisk: source.blogVariants.technicalRisk[resolveTechnicalRiskProfile(selector.title)],
  };
}

function buildLocalizedStaticRouteFaqs(
  routePath: string,
  source: LocalizedFaqSource,
): FaqEntry[] | null {
  const pageMeta = source.pageMeta[routePath];
  if (!pageMeta) {
    return null;
  }

  const values = {
    pageTitle: sanitizeInterpolatedText(pageMeta.title),
    pageDescription: endWithSentenceStop(
      summarize(pageMeta.description, PAGE_DESCRIPTION_SUMMARY_LENGTH),
    ),
  };

  if (routePath === "terms") {
    return buildFaqSetFromLocalizedTemplates(source.templates.terms, values);
  }

  // `privacy` gets its own set for the reason `terms` does: the fall-through
  // below is the generic consulting template, which asks how a topic "creates
  // business value" and which "implementation framework" suits it. On a legal
  // disclosure that reads as filler -- and it would be published twice, as
  // visible prose and as FAQPage JSON-LD. All 17 static routes had a named
  // branch before this one, so `templates.static` is a fall-through nothing
  // reaches; `privacy` would have been the first, on the worst page for it.
  if (routePath === "privacy") {
    return buildFaqSetFromLocalizedTemplates(source.templates.privacy, values);
  }

  if (routePath === "") {
    return buildFaqSetFromLocalizedTemplates(source.templates.home, values);
  }

  if (routePath === "services/consulting") {
    return buildFaqSetFromLocalizedTemplates(source.templates.servicesConsulting, values);
  }

  if (routePath === "services/fixed-price") {
    return buildFaqSetFromLocalizedTemplates(source.templates.servicesFixedPrice, values);
  }

  if (routePath === "services/teams") {
    return buildFaqSetFromLocalizedTemplates(source.templates.servicesTeams, values);
  }

  if (routePath === "services/enablement") {
    return buildFaqSetFromLocalizedTemplates(source.templates.servicesEnablement, values);
  }

  if (routePath === "open-source/kindly-web-search") {
    return buildFaqSetFromLocalizedTemplates(source.templates.openSourceKindlyWebSearch, values);
  }

  if (routePath === "open-source/lad-code-review") {
    return buildFaqSetFromLocalizedTemplates(source.templates.openSourceLadCodeReview, values);
  }

  if (routePath === "open-source/tdd") {
    return buildFaqSetFromLocalizedTemplates(source.templates.openSourceTdd, values);
  }

  if (routePath === "sovereign-ai") {
    return buildFaqSetFromLocalizedTemplates(source.templates.sovereignAi, values);
  }

  if (routePath === "case-studies") {
    return buildFaqSetFromLocalizedTemplates(source.templates.caseStudiesIndex, values);
  }

  if (routePath === "blog") {
    return buildFaqSetFromLocalizedTemplates(source.templates.blogIndex, values);
  }

  if (routePath === "insights") {
    return buildFaqSetFromLocalizedTemplates(source.templates.insightsIndex, values);
  }

  if (routePath === "industries/defence") {
    return buildFaqSetFromLocalizedTemplates(source.templates.industriesDefence, values);
  }

  if (routePath === "industries/pharma") {
    return buildFaqSetFromLocalizedTemplates(source.templates.industriesPharma, values);
  }

  if (routePath === "industries/finance") {
    return buildFaqSetFromLocalizedTemplates(source.templates.industriesFinance, values);
  }

  if (routePath === "industries/enterprise") {
    return buildFaqSetFromLocalizedTemplates(source.templates.industriesEnterprise, values);
  }

  const topic = source.routes[toLocalizedRouteKey(routePath)];
  if (!topic) {
    return null;
  }

  return buildFaqSetFromLocalizedTemplates(source.templates.static, {
    ...values,
    topic,
  });
}

/**
 * Compose the FAQ set for one blog route from its locale's templates.
 *
 * The archetype is chosen here — from the post's tags, category and slug — and
 * the copy it selects comes from the locale file. Until CWS-115 the copy lived
 * in this module as 240 string literals inside `lang === "…"` blocks, which is
 * why 23 of the 49 indexed routes were invisible to every i18n check in the
 * repository.
 *
 * @param routePath - The indexed route path, ``blog/<slug>``.
 * @param lang - The resolved SEO language.
 * @param source - The locale's FAQ source.
 * @returns Five entries, or ``null`` when the slug is not a published post.
 */
function buildLocalizedBlogFaqs(
  routePath: string,
  lang: SeoLanguage,
  source: LocalizedFaqSource,
): FaqEntry[] | null {
  const slug = routePath.replace(/^blog\//, "");
  const post = blogPosts.find((entry) => entry.slug === slug);
  if (!post) {
    return null;
  }

  const title = sanitizeInterpolatedText(getLocalizedField(post, "title", lang));
  const summary = summarize(getLocalizedField(post, "summary", lang));
  const localizedTags = getLocalizedTags(post, lang);
  const visibleTags = localizedTags.filter((tag) => tag.toLowerCase() !== "vendorland");
  const tags = cleanupWhitespace(
    (visibleTags.length > 0 ? visibleTags : localizedTags).join(", "),
  );
  const topic = truncateTopic(title, BLOG_FAQ_TOPIC_MAX_LENGTH);
  const archetype = resolveBlogFaqArchetype(post);
  const templates = source.templates[BLOG_ARCHETYPE_TEMPLATE_KEY[archetype]];
  const values = buildBlogTemplateValues(source, lang, topic, summary, tags, {
    // `post.title` and `post.summary` are the English fields by construction --
    // `getLocalizedField` falls back to them for `en` and for any untranslated
    // locale. Read directly so no language argument reaches a selector at all.
    // Deliberately not passed through `truncateTopic` or `summarize`: both are
    // *rendering* budgets, and a selector should see the whole headline.
    title: sanitizeInterpolatedText(post.title),
    summary: sanitizeInterpolatedText(post.summary),
  });

  const questions = templates.questions.map((value) => fillTemplate(value, values));
  const answers = templates.answers.map((value) => fillTemplate(value, values));
  const companyClauses = templates.companyClauses.map((value) => fillTemplate(value, values));

  // Applied after interpolation and before composition, which is where they
  // were applied before the copy moved: an override replaces a whole rendered
  // string rather than a template.
  const mappedFirstQuestion = getBlogFirstQuestionBySlug(post.slug, lang);
  if (mappedFirstQuestion) {
    questions[0] = mappedFirstQuestion;
  }
  const mappedSecondAnswer = getBlogSecondAnswerOverrideBySlug(post.slug, lang);
  if (mappedSecondAnswer) {
    answers[1] = mappedSecondAnswer;
  }

  return buildFaqSet(questions, answers, companyClauses);
}

function buildLocalizedCaseStudyFaqs(
  routePath: string,
  lang: SeoLanguage,
  source: LocalizedFaqSource,
): FaqEntry[] | null {
  const slug = routePath.replace(/^case-studies\//, "");
  const caseStudy = getLocalizedCaseStudyBySlug(slug, lang);
  if (!caseStudy) {
    return null;
  }

  const values = {
    title: sanitizeInterpolatedText(caseStudy.title),
    client: sanitizeInterpolatedText(caseStudy.client || "the client"),
    challenge: summarize(caseStudy.challenge, 420),
    impact: summarize(caseStudy.impact.join("; "), 520),
    services: cleanupWhitespace(caseStudy.services.join(", ")),
    technologies: cleanupWhitespace(caseStudy.technologies.join(", ")),
  };

  return buildFaqSetFromLocalizedTemplates(source.templates.caseStudy, values);
}

function validateFaqSet(routePath: string, faqs: FaqEntry[]) {
  const expectedCount = getExpectedFaqCountForRoutePath(routePath);
  if (faqs.length !== expectedCount) {
    throw new Error(
      `FAQ set policy violation for "${routePath}": expected ${expectedCount} entries, got ${faqs.length}`,
    );
  }

  return faqs.map((entry) => ({
    question: cleanupWhitespace(entry.question),
    answer: enforceCompanyMentionPolicy(cleanupWhitespace(entry.answer)),
  }));
}

function resolveLocalizedFaqsForIndexedRoutePath(
  routePath: string,
  safeLang: SeoLanguage,
): FaqEntry[] | null {
  const source = getLocalizedFaqSource(safeLang);
  if (!source) {
    throw new Error(`Localized FAQ data is unavailable for language "${safeLang}"`);
  }

  if (routePath.startsWith("blog/") && routePath !== "blog") {
    const blogFaqs = buildLocalizedBlogFaqs(routePath, safeLang, source);
    if (!blogFaqs) {
      throw new Error(
        `Localized FAQ blog template data is missing for route "${routePath}" in "${safeLang}"`,
      );
    }

    return validateFaqSet(routePath, blogFaqs);
  }

  if (routePath.startsWith("case-studies/") && routePath !== "case-studies") {
    const caseStudyFaqs = buildLocalizedCaseStudyFaqs(routePath, safeLang, source);
    if (!caseStudyFaqs) {
      throw new Error(
        `Localized FAQ case-study template data is missing for route "${routePath}" in "${safeLang}"`,
      );
    }

    return validateFaqSet(routePath, caseStudyFaqs);
  }

  const staticFaqs = buildLocalizedStaticRouteFaqs(routePath, source);
  if (!staticFaqs) {
    throw new Error(
      `Localized FAQ route data is missing for route "${routePath}" in "${safeLang}"`,
    );
  }

  return validateFaqSet(routePath, staticFaqs);
}

export function getIndexedRoutePaths() {
  return [...INDEXABLE_ROUTE_PATHS];
}

export function normalizeIndexedRoutePathFromPathname(pathname: string) {
  const normalizedPath = normalizeRoutePath(pathname);
  if (!normalizedPath) {
    return "";
  }

  const segments = normalizedPath.split("/");
  const [firstSegment, ...rest] = segments;

  if (SEO_LANGUAGES.includes(firstSegment as SeoLanguage)) {
    return rest.join("/");
  }

  return normalizedPath;
}

export function resolveFaqsForRoutePath(
  routePath: string,
  language: string,
): FaqEntry[] | null {
  const safeLang = normalizeSeoLanguage(language);
  const normalizedRoutePath = normalizeRoutePath(routePath);

  if (!INDEXED_ROUTE_PATH_SET.has(normalizedRoutePath)) {
    return null;
  }

  return resolveLocalizedFaqsForIndexedRoutePath(normalizedRoutePath, safeLang);
}

export function resolveFaqsForPathname(
  pathname: string,
  language: string,
): FaqEntry[] | null {
  const routePath = normalizeIndexedRoutePathFromPathname(pathname);
  return resolveFaqsForRoutePath(routePath, language);
}
